import { useEffect, type RefObject } from 'react';

/**
 * Gọi `handler` khi click ra ngoài MỌI ref truyền vào. Không làm gì khi
 * `enabled` = false.
 *
 * Nhận nhiều ref vì phần nổi của một widget có thể nằm ở cây DOM khác: menu của
 * `Dropdown` render qua portal ra `body`, nên xét mỗi ref của phần thân là bấm
 * vào một lựa chọn cũng bị tính là "click ra ngoài" — menu đóng ngay ở
 * `mousedown` và cú `click` chọn không bao giờ tới nơi.
 */
export function useClickOutside(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  handler: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    const list = Array.isArray(refs) ? refs : [refs];

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (list.some((ref) => ref.current?.contains(target))) return;
      handler();
    };

    // `mousedown` chứ không phải `click`: phải đóng trước khi phần tử bên dưới
    // kịp nhận sự kiện, nếu không menu đóng rồi mới click xuyên qua.
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mảng ref dựng mới mỗi lần render, so theo phần tử thì effect chạy lại vô hạn
  }, [handler, enabled]);
}
